// =============================================================================
// features/dashboard/components/StatsRow.tsx — Row de 4 KPIs do PERÍODO.
//
// DS §9.8 Stat/KPI: bg-elev-1, elev-2, label uppercase, valor Bricolage 800.
// Hover: Spotlight (halo verde segue cursor via --mx/--my).
//
// Diagnóstico D1 — o que estava errado aqui:
//   1. "Total de leads" imprimia `range.label` sobre uma contagem ALL-TIME.
//      Com "Hoje" selecionado a tela dizia "Total de leads — Hoje: 4.077".
//   2. "Conversão" calculava a taxa sobre `byStatus` all-time e escrevia
//      "sem fechamentos no período" — texto de período sobre número histórico.
//      Pior: em produção `leads.status` nunca chega a closed_won/closed_lost
//      (quem move o lead é o Kanban), então o card dava "—" para sempre.
//
// Agora todo card desta row é do período, e o número que ele exibe é do período.
// A base histórica virou um card próprio na zona "Agora" da página.
// =============================================================================

import * as React from 'react';

import { Stat } from '../../../components/ui/Stat';
import type { LeadsMetrics, RangeInfo } from '../../../hooks/dashboard/types';
import { conversionDisplay, fmtNumber } from '../utils/metrics';

interface StatsRowProps {
  leads: LeadsMetrics;
  range: RangeInfo;
  /**
   * Interações registradas no período.
   *
   * O segundo card exibia `newInRange`, que hoje é o MESMO número do primeiro
   * (ambos "leads criados no período"). Dois cards com o mesmo valor e rótulos
   * diferentes é ruído. Aqui ele passa a mostrar o volume de atendimento — a
   * palavra que a operação de fato usa (achado U7).
   */
  interactionsTotal: number;
}

/**
 * Row de 4 KPIs do período selecionado.
 * Grid responsivo: 1 coluna mobile, 2 colunas tablet, 4 colunas desktop.
 */
export function StatsRow({ leads, range, interactionsTotal }: StatsRowProps): React.JSX.Element {
  // Leads em qualificação ou simulação — agora restrito ao período, porque
  // `byStatus` passou a receber a janela de datas no backend.
  const activeStatuses = ['qualifying', 'simulation'] as const;
  const activeCount = leads.byStatus
    .filter((s) => activeStatuses.includes(s.status as (typeof activeStatuses)[number]))
    .reduce((sum, s) => sum + s.count, 0);

  const conversion = conversionDisplay(leads.conversion, range.label);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Stat
        label="Leads no período"
        value={fmtNumber(leads.total)}
        description={`criados — ${range.label.toLowerCase()}`}
      />
      <Stat
        label="Atendimentos"
        value={fmtNumber(interactionsTotal)}
        description={`interações registradas — ${range.label.toLowerCase()}`}
      />
      <Stat
        label="Em qualificação"
        value={fmtNumber(activeCount)}
        description={
          activeCount > 0
            ? `qualificação + simulação — ${range.label.toLowerCase()}`
            : `nenhum lead nesses estágios em ${range.label.toLowerCase()}`
        }
      />
      <Stat label="Conversão" value={conversion.value} description={conversion.description} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

/** Skeleton respeitando o layout final dos 4 KPIs. Sem layout shift. */
export function StatsRowSkeleton(): React.JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="rounded-md border border-border bg-surface-1 p-5"
          style={{ boxShadow: 'var(--elev-2)', minHeight: '100px' }}
        >
          <div
            className="mb-3 h-2.5 w-24 rounded-pill animate-pulse"
            style={{ background: 'var(--surface-muted)' }}
          />
          <div
            className="h-8 w-16 rounded-xs animate-pulse"
            style={{ background: 'var(--surface-muted)' }}
          />
          <div
            className="mt-2 h-2 w-32 rounded-pill animate-pulse"
            style={{ background: 'var(--border-subtle)' }}
          />
        </div>
      ))}
    </div>
  );
}
